import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';

// Register required Chart.js components
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const BarChart = ({ chartData }) => {
  // Format month string to display only month name
  const formatMonth = (monthStr) => {
    const date = new Date(monthStr);
    return date.toLocaleString('default', { month: 'short' });
  };

  // Process chart data from the provided statistics
  const processChartData = () => {
    if (!chartData || !chartData.monthlyStatus || chartData.monthlyStatus.length === 0) {
      return {
        labels: [],
        datasets: [],
        totals: { paidAmount: 0, overdueAmount: 0, total: 0 },
        lastMonthTotals: { total: 0, clientAmount: 0, providerAmount: 0 },
      };
    }

    const monthlyData = chartData.monthlyStatus;

    // Get last month's data
    const lastMonth = monthlyData[monthlyData.length - 1];

    // Last month's totals for client and provider
    const lastMonthClientAmount = (lastMonth.client_paid_amount || 0) + (lastMonth.client_overdue_amount || 0);
    const lastMonthProviderAmount = (lastMonth.provider_paid_amount || 0) + (lastMonth.provider_overdue_amount || 0);
    const lastMonthTotal = lastMonthClientAmount + lastMonthProviderAmount;

    // Calculate overall totals
    const totals = monthlyData.reduce((acc, month) => {
      acc.clientPaidAmount += month.client_paid_amount || 0;
      acc.clientOverdueAmount += month.client_overdue_amount || 0;
      acc.providerPaidAmount += month.provider_paid_amount || 0;
      acc.providerOverdueAmount += month.provider_overdue_amount || 0;
      acc.total += (month.client_paid_amount || 0) + (month.client_overdue_amount || 0) + (month.provider_paid_amount || 0) + (month.provider_overdue_amount || 0);
      return acc;
    }, { clientPaidAmount: 0, clientOverdueAmount: 0, providerPaidAmount: 0, providerOverdueAmount: 0, total: 0 });

    return {
      labels: monthlyData.map(item => formatMonth(item.month)),
      datasets: [
        {
          label: 'Client Paid Amount',
          data: monthlyData.map(item => item.client_paid_amount || 0),
          backgroundColor: '#69C195',
          borderRadius: 8,
          barThickness: 30,
        },
        {
          label: 'Client Overdue Amount',
          data: monthlyData.map(item => item.client_overdue_amount || 0),
          backgroundColor: '#B8E0FF',
          borderRadius: 8,
          barThickness: 30,
        },
        {
          label: 'Provider Paid Amount',
          data: monthlyData.map(item => item.provider_paid_amount || 0),
          backgroundColor: '#5AB8ED',
          borderRadius: 8,
          barThickness: 30,
        },
        {
          label: 'Provider Overdue Amount',
          data: monthlyData.map(item => item.provider_overdue_amount || 0),
          backgroundColor: '#F0A500',
          borderRadius: 8,
          barThickness: 30,
        },
      ],
      totals,
      lastMonthTotals: {
        total: lastMonthTotal,
        clientAmount: lastMonthClientAmount,
        providerAmount: lastMonthProviderAmount,
      },
    };
  };

  const { labels, datasets, totals, lastMonthTotals } = processChartData();

  // Configuration options for the chart
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => `$${tooltipItem.raw}`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 0,
        },
        categoryPercentage: 0.7,
        barPercentage: 0.6,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          callback: (value) => `$${value}`,
        },
      },
    },
  };

  // Data object for the chart
  const data = {
    labels,
    datasets,
  };

  // Render the chart with a custom container
  return (
    <div style={{ width: '100%', height: '400px', margin: '0 auto', paddingTop: '60px', position: 'relative' }}>
      {/* Title on the left side */}
      <div style={{ 
        position: 'absolute', 
        top: '0', 
        left: '0', 
      }}>
        <span className='text-gray-600 font-medium text-xs'>Total Revenue</span> 
        <div className='text-gray-600 font-semibold text-sm'>${totals.total.toLocaleString()}</div> 
      </div>

      {/* Custom Legend on the right side */}
      <div style={{ 
        position: 'absolute', 
        top: '0', 
        right: '0', 
        display: 'flex', 
        gap: '30px',
        alignItems: 'center',
      }}>
        <div className='flex gap-3'>
          <div className='mt-2' style={{ width: '8px', height: '8px', backgroundColor: '#69C195', borderRadius: '50%' }}></div>
          <div>
            <span className='text-gray-600 font-medium text-xs'>Monthly Total</span>
            <div className='text-gray-600 font-semibold text-sm'>${lastMonthTotals.total.toLocaleString()}</div>
          </div>
        </div>
        <div className='flex gap-3'>
          <div className='mt-2' style={{ width: '8px', height: '8px', backgroundColor: '#B8E0FF', borderRadius: '50%' }}></div>
          <div>
            <span className='text-gray-600 font-medium text-xs'>Clients</span>
            <div className='text-gray-600 font-semibold text-sm'>${lastMonthTotals.clientAmount.toLocaleString()}</div>
          </div>
        </div>
        <div className='flex gap-3'>
          <div className='mt-2' style={{ width: '8px', height: '8px', backgroundColor: '#5AB8ED', borderRadius: '50%' }}></div>
          <div>
            <span className='text-gray-600 font-medium text-xs'>Providers</span>
            <div className='text-gray-600 font-semibold text-sm'>${lastMonthTotals.providerAmount.toLocaleString()}</div>
          </div>
        </div>
      </div>
    
      {/* Chart */}
      <Bar data={data} options={options} />
    </div>
  );
};

export default BarChart;

// Usage example
// <BarChart chartData={reportData} />
